import { BroadcastService } from '@app/core/service';
import { Component, OnInit, OnDestroy, ChangeDetectorRef, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription, tap } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

import { MessageService } from '@app/service/message.service';
import { ByocService } from '@app/byoc/popup/service/byoc.service';
import { ByocCourseService } from '../../service/course.service';
import { Mission } from '@app/core/model/library.model';
import { ByocEpisodePopupService } from '../../../episode/service/popup.service';

@Component({
    selector: 'app-byoc-course-step-position',
    templateUrl: './position.component.html',
    styleUrls: ['./position.component.scss'],
    standalone: false
})
export class ByocCourseStepPositionComponent implements OnInit, OnDestroy {
    private readonly router = inject(Router);
    private readonly changeDetectorRef = inject(ChangeDetectorRef);
    private readonly translateService = inject(TranslateService);
    private readonly messageService = inject(MessageService);
    private readonly byocService = inject(ByocService);
    private readonly byocCourseService = inject(ByocCourseService);
    private readonly broadcastService = inject(BroadcastService);
    private readonly byocEpisodePopupService = inject(ByocEpisodePopupService);

    mission!: Mission;
    loaded = false;
    loadedList = false;
    saving = false;

    private subscriptions: Subscription = new Subscription();

    get canOpenMission() {
        return this.mission.canOpen;
    }

    get isSubmodalOfAddingMission(): boolean {
        return this.byocCourseService.isSubmodal;
    }

    ngOnInit() {
        this.subscriptions.add(
            this.byocService
                .mission(this.byocCourseService.form.value.missionId)
                .pipe(
                    tap((mission) => {
                        this.mission = mission;
                        this.loaded = true;
                        this.changeDetectorRef.detectChanges();
                    }),
                )
                .subscribe(),
        );
    }

    ngOnDestroy() {
        this.subscriptions.unsubscribe();
    }

    goToMission(missionId: string) {
        this.router.navigate(['/library', 'mission', missionId]);
    }

    listLoaded($event: boolean) {
        this.loadedList = $event;
    }

    cancelClick(): void {
        this.byocCourseService.close('btn-close');
    }

    backClick(): void {
        if (this.byocCourseService.skipSteps.includes('mission')) {
            this.byocCourseService.step = 'general';
        } else {
            this.byocCourseService.step = 'mission';
        }

        this.changeDetectorRef.markForCheck();
    }

    saveAsDraftClick(): void {
        if (!this.loaded) {
            return;
        }

        // set as draft
        this.byocCourseService.form.patchValue({ status: 1 });

        // show loader
        this.saving = true;
        this.changeDetectorRef.detectChanges();

        // save as draft and redirect with message
        this.subscriptions.add(
            this.byocService
                .createCourse(this.byocCourseService.form.getRawValue())
                .pipe(
                    tap((course) => {
                        this.messageService.setRouteMessage(
                            this.translateService.instant('v3-byoc-popup-create-course-alert-save-as-draft', { name: course.name }),
                            'success',
                        );
                        this.router.navigate(['/settings', 'edit']);
                        this.byocCourseService.close('btn-close');
                    }),
                )
                .subscribe(),
        );
    }

    saveClick(): void {
        this.byocCourseService.step = 'warning';
    }

    saveAndAddClick(): void {
        if (!this.loaded) {
            return;
        }

        // set as publish
        this.byocCourseService.form.patchValue({ status: 2 });

        // show loader
        this.saving = true;
        this.changeDetectorRef.detectChanges();

        // save and create Episode
        this.subscriptions.add(
            this.byocService
                .createCourse(this.byocCourseService.form.getRawValue())
                .pipe(
                    tap((course) => {
                        this.messageService.setMessage(
                            this.translateService.instant('v3-byoc-popup-create-course-alert-created', { name: course.name }),
                            'info',
                        );
                        this.broadcastService.send('popup-added-course-without-li');
                        this.byocEpisodePopupService.replacePopup({
                            type: null,
                            courseId: course.id,
                            isFromOutside: true,
                            skipPosition: false,
                            uploadContentType: null,
                            episodeToEditId: null,
                            episodeToEditData: null,
                        });
                    }),
                )
                .subscribe(),
        );
    }
}
